/**
 * 計算フィールド 計算エンジン（#3145）の評価器。
 * 型変換・空値伝播・エラー処理のルールは設計書 §4.3 に従う。
 * ⚠️ functions/src/utils/calculation と main/src/utils/calculation は同一内容。
 *    片方だけ変更しないこと（変更時は両方へ反映し、共通テストで一致を確認する）。
 */
import { calculationErrorCode } from './constants';
import { CalculationFormulaError } from './calculationError';
import {
  CalculationAggregateValues,
  CalculationFieldValues,
  CalculationResult,
  EvaluatedValue,
} from './types';
import { AstNode, parseFormula } from './parser';
import { toNumericValue, toTextValue } from './conversion';
import { applyCalculationFunction } from './functions';

/** 数値演算の結果が数値として扱えるか検証する（0除算由来の Infinity 等を弾く） */
const assertFiniteResult = (value: number): number => {
  if (Number.isNaN(value)) {
    throw new CalculationFormulaError(calculationErrorCode.notNumber, '計算結果が数値になりません');
  }
  if (!Number.isFinite(value)) {
    throw new CalculationFormulaError(calculationErrorCode.limitExceeded, '計算結果が大きすぎます');
  }
  return value;
};

/**
 * IF の条件が真か（#3358）。
 * 数値なら 0 以外が真（比較演算子は 1 / 0 を返すためこれで繋がる）。
 * 文字列は空文字以外が真。空値は偽。
 */
const isTruthy = (value: EvaluatedValue): boolean => {
  if (value === null) return false;
  if (typeof value === 'number') return value !== 0;
  return value.trim() !== '';
};

/** 比較演算子（#3358）の一覧 */
const COMPARISON_OPERATORS: string[] = ['=', '!=', '<', '>', '<=', '>='];

/**
 * 比較用に数値へ変換する。変換できなければ null を返す（エラーにしない）。
 *
 * 四則演算の toNumericValue はエラーを投げるが、比較では
 * 「数値にできないなら文字列として比べる」ため投げてはいけない。
 */
const toComparableNumber = (value: EvaluatedValue): number | null => {
  if (value === null) return null;
  if (typeof value === 'number') return value;
  const normalized = value.trim().replace(/,/g, '');
  if (normalized === '') return null;
  return NUMERIC_COMPARE_PATTERN.test(normalized) ? Number(normalized) : null;
};

/** 比較で数値として扱える形式（toNumericValue の NUMERIC_PATTERN と同じ） */
const NUMERIC_COMPARE_PATTERN = /^[+-]?[0-9]+(\.[0-9]+)?$/;

/** 真偽を計算式の値（1 / 0）にする。真偽値型を足さずに既存の数値経路へ乗せる */
const toComparisonResult = (matched: boolean): EvaluatedValue => (matched ? 1 : 0);

/**
 * 比較を評価する（#3358）。
 *
 * 両辺が数値として解釈できれば数値比較、そうでなければ文字列比較にする。
 * カンマ付きの数値（"1,000"）は数値として扱うため "1,000" = "1000" は真になる。
 *
 * 空値の扱いは四則演算（設計書 §4.3）に揃える。
 * - = / != は空値同士なら一致、片方だけ空なら不一致
 * - 大小比較は空値を含むと**空を返す**（比較できない）。
 *   0 とみなすと「出勤 0 < 退勤 0」のような誤判定が起きるため
 */
const evaluateComparison = (
  operator: string,
  left: EvaluatedValue,
  right: EvaluatedValue,
): EvaluatedValue => {
  const leftEmpty = left === null || left === '';
  const rightEmpty = right === null || right === '';

  if (operator === '=' || operator === '!=') {
    if (leftEmpty || rightEmpty) {
      const equal = leftEmpty && rightEmpty;
      return toComparisonResult(operator === '=' ? equal : !equal);
    }
  } else if (leftEmpty || rightEmpty) {
    // 大小比較は比較材料が足りないため空を返す
    return null;
  }

  // 両辺が数値なら数値比較。片方でも数値にならなければ文字列として比べる。
  // toNumericValue は変換できないとエラーを投げるため、比較では使えない
  const leftNumber = toComparableNumber(left);
  const rightNumber = toComparableNumber(right);
  const useNumber = leftNumber !== null && rightNumber !== null;
  const a: number | string = useNumber ? leftNumber : toTextValue(left);
  const b: number | string = useNumber ? rightNumber : toTextValue(right);

  switch (operator) {
    case '=':
      return toComparisonResult(a === b);
    case '!=':
      return toComparisonResult(a !== b);
    case '<':
      return toComparisonResult(a < b);
    case '>':
      return toComparisonResult(a > b);
    case '<=':
      return toComparisonResult(a <= b);
    case '>=':
      return toComparisonResult(a >= b);
    default:
      return null;
  }
};

const evaluateBinary = (
  node: Extract<AstNode, { type: 'binary' }>,
  fieldValues: CalculationFieldValues,
  aggregateValues?: CalculationAggregateValues,
): EvaluatedValue => {
  const left = evaluateNode(node.left, fieldValues, aggregateValues);
  const right = evaluateNode(node.right, fieldValues, aggregateValues);

  // 文字列結合: 空値は空文字として結合する
  if (node.operator === '&') {
    return toTextValue(left) + toTextValue(right);
  }

  // 比較（#3358）。結果は 1 / 0 の数値で返す（設計書 §5.1-①）
  if (COMPARISON_OPERATORS.includes(node.operator)) {
    return evaluateComparison(node.operator, left, right);
  }

  // 四則演算・べき乗: 片方でも空値なら結果を空にする（0 扱いにしない。設計書 §4.3）
  const leftNumber = toNumericValue(left);
  const rightNumber = toNumericValue(right);
  if (leftNumber === null || rightNumber === null) {
    return null;
  }

  switch (node.operator) {
    case '+':
      return assertFiniteResult(leftNumber + rightNumber);
    case '-':
      return assertFiniteResult(leftNumber - rightNumber);
    case '*':
      return assertFiniteResult(leftNumber * rightNumber);
    case '/':
      if (rightNumber === 0) {
        throw new CalculationFormulaError(calculationErrorCode.divideByZero);
      }
      return assertFiniteResult(leftNumber / rightNumber);
    case '^':
      return assertFiniteResult(Math.pow(leftNumber, rightNumber));
    default:
      // 比較演算子は上で処理済みのためここには来ない
      return null;
  }
};

/**
 * SUM を評価する（#3358）。明細の行をまたいで合計する。
 *
 * 引数は集約参照（{明細.金額}）であること。通常の参照や式は受け付けない
 * （設計書 §5.1-②「式を含む集約は非対応」。行ごとの計算列を経由してもらう）。
 *
 * 空値の行はスキップする（0 として足す）。四則演算の空値伝播とは
 * **意図的に異なる**扱いで、1行空だから合計が空になるのは実用に合わないため。
 * 明細が 0 行なら 0 を返す（空ではない）。
 */
const evaluateSum = (
  args: AstNode[],
  fieldValues: CalculationFieldValues,
  aggregateValues?: CalculationAggregateValues,
): EvaluatedValue => {
  let total = 0;
  for (const arg of args) {
    if (arg.type !== 'aggregateRef') {
      throw new CalculationFormulaError(
        calculationErrorCode.syntax,
        'SUM の引数には明細の項目（{明細.金額} の形）を指定してください',
      );
    }
    const values = aggregateValues?.[arg.rawCode];
    if (values === undefined) {
      throw new CalculationFormulaError(
        calculationErrorCode.aggregateNotFound,
        `項目: {${arg.rawCode}}`,
      );
    }
    for (const value of values) {
      // 空値の行は合計に含めない（0 として扱う）
      if (value === null || value === undefined || value.trim() === '') continue;
      const num = toNumericValue(value);
      if (num === null) continue;
      total += num;
    }
  }
  return assertFiniteResult(total);
};

const evaluateNode = (
  node: AstNode,
  fieldValues: CalculationFieldValues,
  aggregateValues?: CalculationAggregateValues,
): EvaluatedValue => {
  switch (node.type) {
    case 'number':
      return node.value;
    case 'string':
      return node.value;
    case 'fieldRef': {
      if (!Object.prototype.hasOwnProperty.call(fieldValues, node.fieldCode)) {
        throw new CalculationFormulaError(
          calculationErrorCode.unknownField,
          `項目: {${node.fieldCode}}`,
        );
      }
      const value = fieldValues[node.fieldCode];
      // null / undefined / 空文字は空値として扱う
      return value === null || value === undefined || value === '' ? null : value;
    }
    case 'aggregateRef':
      // 集約参照は SUM の引数としてのみ意味を持つ（#3358）
      throw new CalculationFormulaError(
        calculationErrorCode.aggregateOutOfScope,
        `項目: {${node.rawCode}}`,
      );
    case 'unary': {
      const operand = toNumericValue(evaluateNode(node.operand, fieldValues, aggregateValues));
      if (operand === null) {
        return null;
      }
      return node.operator === '-' ? -operand : operand;
    }
    case 'binary':
      return evaluateBinary(node, fieldValues, aggregateValues);
    case 'funcCall': {
      // IF は短絡評価する（#3358）。偽の枝まで評価すると
      // IF({数量} = 0, "", {金額} / {数量}) で 0 除算エラーが立ち、
      // 「IF で例外ケースを避ける」という最も自然な用途が成立しなくなる
      if (node.name === 'IF') {
        const condition = evaluateNode(node.args[0], fieldValues, aggregateValues);
        return evaluateNode(
          isTruthy(condition) ? node.args[1] : node.args[2],
          fieldValues,
          aggregateValues,
        );
      }
      // AND / OR も短絡評価する（#3358）。IF と同じ理由で、結果が決まった
      // 時点で打ち切る。AND({数量} > 0, {金額} / {数量} > 100) のように
      // 前の条件で後ろを守る書き方が成立しなくなるため
      if (node.name === 'AND') {
        for (const arg of node.args) {
          if (!isTruthy(evaluateNode(arg, fieldValues, aggregateValues))) {
            return toComparisonResult(false);
          }
        }
        return toComparisonResult(true);
      }
      if (node.name === 'OR') {
        for (const arg of node.args) {
          if (isTruthy(evaluateNode(arg, fieldValues, aggregateValues))) {
            return toComparisonResult(true);
          }
        }
        return toComparisonResult(false);
      }
      if (node.name === 'NOT') {
        return toComparisonResult(
          !isTruthy(evaluateNode(node.args[0], fieldValues, aggregateValues)),
        );
      }
      // SUM は集約参照を引数に取るため、専用の評価をする（#3358）
      if (node.name === 'SUM') {
        return evaluateSum(node.args, fieldValues, aggregateValues);
      }
      const args = node.args.map(arg => evaluateNode(arg, fieldValues, aggregateValues));
      return applyCalculationFunction(node.name, args);
    }
  }
};

/**
 * 計算式を評価する。
 * エラー時は value が空文字になり error にエラー内容が入る（OCR 処理は止めない。設計書 §4.3）
 */
export const evaluateFormula = (
  formula: string,
  fieldValues: CalculationFieldValues,
  /**
   * 明細の集計（SUM）用の値（#3358）。省略すると集約参照はエラーになる。
   * 既存の呼び出し側は変更不要
   */
  aggregateValues?: CalculationAggregateValues,
): CalculationResult => {
  try {
    const ast = parseFormula(formula);
    const value = evaluateNode(ast, fieldValues, aggregateValues);
    return { value: toTextValue(value), error: null };
  } catch (error) {
    if (error instanceof CalculationFormulaError) {
      return { value: '', error: { code: error.code, message: error.message } };
    }
    throw error;
  }
};
